import { useLocation, useParams } from 'react-router-dom';
import { useContext, useState, useEffect } from 'react';
import { CHANNEL_CONFIGS } from 'domains/landing-autos/constants/channels';
import { ESSENTIAL_PLAN_OFFER_CODE } from 'domains/landing-autos/constants/new-plans';
import Utils from 'libs/common/utils';
import { AppContext } from 'domains/commons/contexts/global/global-provider';
import { usePatchCustomer } from 'domains/core-base/customer/hooks/mutations/use-patch-customer';
import { useQuotationFull } from 'domains/core-base/quotation/hooks/mutations/use-quotation-full';
import { useQuotationEssential } from 'domains/core-base/quotation/hooks/mutations/use-quotation-essential';
import { DataLayer } from 'libs/providers/datalayer/datalayer';
import {
  globalFlow,
  VIRTUAL_EVENT_ACTIONS,
  VIRTUAL_EVENT_CATEGORIES,
  VIRTUAL_EVENT_SECTION,
  VIRTUAL_EVENT_SUB_ZONE,
  VIRTUAL_EVENT_TYPE_ELEMENT,
  VIRTUAL_EVENT_TYPE_EVENT,
  VIRTUAL_EVENT_TYPE_ZONE,
  VIRTUAL_EVENT_ZONE,
} from 'libs/providers/datalayer/constants';
import encryptSha256 from 'libs/formats/encrypt/sha256';
import ROUTES from 'libs/constants/routes';
import {
  CUSTOMER_ACTION,
  QUOTATION_ACTION,
} from 'domains/commons/contexts/global/staticData';

export const useModalCustomerEdit = ({ handleClose, isOpen }: any) => {
  const location = useLocation();
  const { channel } = useParams();
  const { store, actions } = useContext(AppContext);
  const customerMutation = usePatchCustomer();
  const quotationFullMutation = useQuotationFull();
  const essentialQuoteMutation = useQuotationEssential();

  const [spinner, setSpinner] = useState(false);

  const [fields, setFields] = useState({
    phone: '',
    phoneValid: false,
    email: '',
    emailValid: false,
  });
  const formValid = Object.entries(fields).every(([_, value]) => value);

  const changeValidState = (key: string, valid: boolean) => {
    setFields((state) => ({ ...state, [key]: valid }));
  };

  const handleClickClose = () => {
    DataLayer.setVirtualEvent({
      category: VIRTUAL_EVENT_CATEGORIES.default,
      action:
        location.pathname === ROUTES.STEP2 ||
        location.pathname === ROUTES.STEP2_MIRROW
          ? VIRTUAL_EVENT_ACTIONS.cotizacion
          : VIRTUAL_EVENT_ACTIONS.resumen,
      label: `Botón - Cerrar`,
      tipoZona: VIRTUAL_EVENT_TYPE_ZONE.autos,
      zona: VIRTUAL_EVENT_ZONE.nuevo,
      subZona:
        location.pathname === ROUTES.STEP2 ||
        location.pathname === ROUTES.STEP2_MIRROW
          ? VIRTUAL_EVENT_SUB_ZONE.paso1
          : VIRTUAL_EVENT_SUB_ZONE.paso2,
      sección: VIRTUAL_EVENT_SECTION.formulario,
      tipoEvento: VIRTUAL_EVENT_TYPE_EVENT.click,
      tipoElemento: VIRTUAL_EVENT_TYPE_ELEMENT.modalBoton,
      valor: 'Actualicemos tus datos:Cerrar',
      flujo: globalFlow(store.dataLayer),
    });
    handleClose();
  };

  const updateEssentialQuote = async () => {
    const { primaryChannel, secondaryChannel } =
      CHANNEL_CONFIGS[channel ?? ''] || {};
    const quotation = await essentialQuoteMutation.mutateAsync({
      circulationAreaCode: store.vehicle?.circulationAreaCode,
      primaryChannel,
      secondaryChannel,
      vehicleId: store.vehicle?.id,
      clientId: String(store.customer?.id),
    });

    actions.dispatchQuotation({
      type: QUOTATION_ACTION.set,
      payload: {
        ...quotation,
        offerCode: store.quotation?.offerCode,
      },
    });
  };

  const handleBtnClick = async () => {
    setSpinner(true);

    const body: any = {
      id: store.customer.id,
      email: fields.email,
      mobile: fields.phone,
    };

    await customerMutation.mutateAsync(body);

    actions.dispatchCustomer({
      type: CUSTOMER_ACTION.set,
      payload: body,
    });

    if (store.plan.offerCode) {
      if (store.plan.offerCode === ESSENTIAL_PLAN_OFFER_CODE) {
        await updateEssentialQuote();
      } else {
        const quotation = await quotationFullMutation.mutateAsync({
          clientId: store.customer.id,
          requestId: store.request.id,
          vehicleId: store.vehicle.id,
          offerCode: store.plan.offerCode,
          promotionalCode: '',
          typeOfQuote: 'full',
        });

        actions.dispatchQuotation({
          type: QUOTATION_ACTION.set,
          payload: quotation,
        });
      }
    }

    DataLayer.setVirtualEvent({
      category: VIRTUAL_EVENT_CATEGORIES.default,
      action:
        location.pathname === ROUTES.STEP2 ||
        location.pathname === ROUTES.STEP2_MIRROW
          ? VIRTUAL_EVENT_ACTIONS.cotizacion
          : VIRTUAL_EVENT_ACTIONS.resumen,
      label: `Botón - Continuar`,
      tipoZona: VIRTUAL_EVENT_TYPE_ZONE.autos,
      zona: VIRTUAL_EVENT_ZONE.nuevo,
      subZona:
        location.pathname === ROUTES.STEP2 ||
        location.pathname === ROUTES.STEP2_MIRROW
          ? VIRTUAL_EVENT_SUB_ZONE.paso1
          : VIRTUAL_EVENT_SUB_ZONE.paso2,
      sección: VIRTUAL_EVENT_SECTION.formulario,
      tipoEvento: VIRTUAL_EVENT_TYPE_EVENT.click,
      tipoElemento: VIRTUAL_EVENT_TYPE_ELEMENT.modalBoton,
      valor: 'Actualicemos tus datos:Continuar',
      flujo: globalFlow(store.dataLayer),
      extra: {
        numeroCelular: encryptSha256(fields.phone),
        correo: encryptSha256(fields.email),
      },
    });

    handleClose();
  };

  const handleChange = (e: any, key: string) => {
    const data = {
      ...fields,
      [key]:
        key === 'phone' ? Utils.stripNumbers(e.target.value) : e.target.value,
    };
    setFields(data);
  };

  useEffect(() => {
    if (isOpen) {
      DataLayer.setVirtualEvent({
        category: VIRTUAL_EVENT_CATEGORIES.default,
        action:
          location.pathname === ROUTES.STEP2 ||
          location.pathname === ROUTES.STEP2_MIRROW
            ? VIRTUAL_EVENT_ACTIONS.cotizacion
            : VIRTUAL_EVENT_ACTIONS.resumen,
        label: `Modal - Actualicemos tus datos`,
        tipoZona: VIRTUAL_EVENT_TYPE_ZONE.autos,
        zona: VIRTUAL_EVENT_ZONE.nuevo,
        subZona:
          location.pathname === ROUTES.STEP2 ||
          location.pathname === ROUTES.STEP2_MIRROW
            ? VIRTUAL_EVENT_SUB_ZONE.paso1
            : VIRTUAL_EVENT_SUB_ZONE.paso2,
        sección: VIRTUAL_EVENT_SECTION.modal,
        tipoEvento: VIRTUAL_EVENT_TYPE_EVENT.visualizar,
        tipoElemento: VIRTUAL_EVENT_TYPE_ELEMENT.modal,
        valor: 'Actualicemos tus datos',
        flujo: globalFlow(store.dataLayer),
      });
    }
  }, [isOpen]);

  return {
    fields,
    changeValidState,
    formValid,
    handleBtnClick,
    handleChange,
    spinner,
    handleClickClose,
  };
};
